import React, { useState, useEffect } from 'react'
import { AlertCircle, Plus, Trash2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Select,
  SelectOption,
  Checkbox,
  Label,
  Textarea,
} from '../ui'
import { useCreatePosition, useUpdatePosition, type Position, type PositionDutyItem } from '../../hooks/usePositions'
import { useDepartmentsForSelect } from '../../hooks/useDepartments'
import { useOrganizationsForSelect } from '../../hooks/organizations'
import { LoadingSpinner } from '../ui/loading-states'

interface PositionFormData {
  name: string
  organization_id?: string | null
  department_id?: string | null
  code_1c?: string | null
  can_manage_schedule?: boolean
  description?: string
  duties: PositionDutyItem[]
}

const DUTY_CATEGORIES = [
  { value: 'duties', label: 'Обязанности' },
  { value: 'requirements', label: 'Требования' },
  { value: 'skills', label: 'Навыки' },
] as const

interface PositionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  position?: Position | null
  mode: 'create' | 'edit'
}

export function PositionForm({ open, onOpenChange, position, mode }: PositionFormProps) {
  const [formData, setFormData] = useState<PositionFormData>({
    name: '',
    organization_id: null,
    department_id: null,
    code_1c: null,
    can_manage_schedule: false,
    description: '',
    duties: [],
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Load organizations for selection
  const { data: organizations } = useOrganizationsForSelect()

  // Load departments for selection - фильтруем по выбранной организации
  const { data: departments } = useDepartmentsForSelect(formData.organization_id)

  const createPosition = useCreatePosition()
  const updatePosition = useUpdatePosition()

  const isLoading = createPosition.isPending || updatePosition.isPending

  // Initialize form data when dialog opens or position changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && position) {
        setFormData({
          name: position.name || '',
          organization_id: position.organization_id || null,
          department_id: position.department_id || null,
          code_1c: position.code_1c || null,
          can_manage_schedule: position.can_manage_schedule ?? false,
          description: position.description || '',
          duties: Array.isArray(position.duties) ? position.duties : [],
        })
      } else {
        setFormData({
          name: '',
          organization_id: null,
          department_id: null,
          code_1c: null,
          can_manage_schedule: false,
          description: '',
          duties: [],
        })
      }
      setErrors({})

      // Сброс ошибок при открытии формы
      createPosition.reset()
      updatePosition.reset()
    }
  }, [open, mode, position]) // Убрал createPosition и updatePosition из зависимостей

  const handleInputChange = (field: keyof PositionFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    let value: string | number | null = e.target.value

    // Convert empty string to null for optional fields
    if (value === '' && (field === 'organization_id' || field === 'department_id' || field === 'code_1c')) {
      value = null
    }

    // При смене организации сбрасываем выбранное подразделение
    if (field === 'organization_id') {
      setFormData(prev => ({ ...prev, [field]: value, department_id: null }))
    } else {
      setFormData(prev => ({ ...prev, [field]: value }))
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Название должности обязательно'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Название должно содержать минимум 2 символа'
    }


    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      // Prepare data for sending
      const dataToSend: any = {
        name: formData.name.trim(),
      }

      // Handle optional fields - передаем null для пустых значений
      if (formData.organization_id && formData.organization_id.trim() !== '') {
        dataToSend.organization_id = formData.organization_id.trim()
      } else {
        dataToSend.organization_id = null
      }

      if (formData.department_id && formData.department_id.trim() !== '') {
        dataToSend.department_id = formData.department_id.trim()
      } else {
        dataToSend.department_id = null
      }

      if (formData.code_1c && formData.code_1c.trim() !== '') {
        dataToSend.code_1c = formData.code_1c.trim()
      } else {
        dataToSend.code_1c = null
      }

      // Handle boolean field
      dataToSend.can_manage_schedule = formData.can_manage_schedule ?? false

      // Handle description
      dataToSend.description = formData.description?.trim() || null

      // Handle duties - filter out empty items
      dataToSend.duties = formData.duties.filter(d => d.text.trim()).map(d => ({
        text: d.text.trim(),
        category: d.category || 'duties',
      }))

      if (mode === 'create') {
        await createPosition.mutateAsync(dataToSend)
      } else if (mode === 'edit' && position) {
        await updatePosition.mutateAsync({
          id: position.id,
          data: dataToSend
        })
      }

      // Закрываем форму только если операция прошла успешно
      onOpenChange(false)
    } catch (error) {
      console.error('Form submission error:', error)
      // Форма остается открытой чтобы показать ошибку
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Создать должность' : 'Редактировать должность'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Заполните информацию для создания новой должности.'
              : 'Измените информацию о должности.'
            }
          </DialogDescription>
          {!isLoading && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Position Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Название должности *
            </label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange('name')}
              placeholder="Введите название должности"
              disabled={isLoading}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <div className="flex items-center mt-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.name}
              </div>
            )}
          </div>

          {/* Organization */}
          <div>
            <label htmlFor="organization_id" className="block text-sm font-medium text-gray-700 mb-1">
              Организация
            </label>
            <Select
              id="organization_id"
              value={formData.organization_id || ''}
              onChange={handleInputChange('organization_id')}
              disabled={isLoading}
            >
              <SelectOption value="">Не назначена</SelectOption>
              {organizations?.map((org: { id: string; name: string; is_headquarters: boolean }) => (
                <SelectOption key={org.id} value={org.id}>
                  {org.name} {org.is_headquarters ? '(головная)' : ''}
                </SelectOption>
              ))}
            </Select>
          </div>

          {/* Department */}
          <div>
            <label htmlFor="department_id" className="block text-sm font-medium text-gray-700 mb-1">
              Подразделение
            </label>
            <Select
              id="department_id"
              value={formData.department_id || ''}
              onChange={handleInputChange('department_id')}
              disabled={isLoading || !formData.organization_id}
            >
              <SelectOption value="">{formData.organization_id ? 'Не указано' : 'Сначала выберите организацию'}</SelectOption>
              {departments?.map(dept => (
                <SelectOption key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectOption>
              ))}
            </Select>
          </div>

          {/* 1C Code */}
          <div>
            <label htmlFor="code_1c" className="block text-sm font-medium text-gray-700 mb-1">
              Код 1С
            </label>
            <Input
              id="code_1c"
              type="text"
              value={formData.code_1c || ''}
              onChange={handleInputChange('code_1c')}
              placeholder="Код из системы 1С"
              disabled={isLoading}
            />
          </div>

          {/* Can Manage Schedule */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="can-manage-schedule"
              checked={formData.can_manage_schedule || false}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, can_manage_schedule: checked === true }))}
              disabled={isLoading}
            />
            <Label htmlFor="can-manage-schedule" className="font-normal cursor-pointer">
              Управление графиком
            </Label>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Описание должности
            </label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={handleInputChange('description')}
              placeholder="Общее описание должности и зоны ответственности"
              disabled={isLoading}
              rows={3}
            />
          </div>

          {/* Duties */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Функционал должности
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  duties: [...prev.duties, { text: '', category: 'duties' }],
                }))}
                disabled={isLoading}
              >
                <Plus className="h-4 w-4 mr-1" />
                Добавить
              </Button>
            </div>

            {formData.duties.length === 0 && (
              <p className="text-sm text-gray-400 italic">
                Нет пунктов. Нажмите «Добавить» чтобы указать обязанности, требования или навыки.
              </p>
            )}

            <div className="space-y-2">
              {formData.duties.map((duty, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="flex-1">
                    <Input
                      value={duty.text}
                      onChange={(e) => {
                        const newDuties = [...formData.duties]
                        newDuties[index] = { ...newDuties[index], text: e.target.value }
                        setFormData(prev => ({ ...prev, duties: newDuties }))
                      }}
                      placeholder="Описание пункта"
                      disabled={isLoading}
                    />
                  </div>
                  <Select
                    value={duty.category || 'duties'}
                    onChange={(e) => {
                      const newDuties = [...formData.duties]
                      newDuties[index] = { ...newDuties[index], category: e.target.value as PositionDutyItem['category'] }
                      setFormData(prev => ({ ...prev, duties: newDuties }))
                    }}
                    disabled={isLoading}
                    className="w-[150px] flex-shrink-0"
                  >
                    {DUTY_CATEGORIES.map(cat => (
                      <SelectOption key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectOption>
                    ))}
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const newDuties = formData.duties.filter((_, i) => i !== index)
                      setFormData(prev => ({ ...prev, duties: newDuties }))
                    }}
                    disabled={isLoading}
                    className="flex-shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Show API errors */}
          {(createPosition.error || updatePosition.error) && (
            <div className="flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(() => {
                  const error = (createPosition.error || updatePosition.error) as any
                  const message = error?.message
                  const status = error?.status

                  if (status === 400) {
                    return 'Проверьте правильность заполнения полей'
                  } else if (status === 409) {
                    return 'Должность с таким названием уже существует'
                  } else if (status === 403) {
                    return 'У вас нет прав для выполнения этой операции'
                  } else if (status === 500) {
                    return 'Произошла ошибка на сервере. Попробуйте позже'
                  } else {
                    return message || `Произошла ошибка при ${mode === 'create' ? 'создании' : 'обновлении'} должности`
                  }
                })()}
              </span>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Отменить
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="min-w-[100px]"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  {mode === 'create' ? 'Создание...' : 'Сохранение...'}
                </>
              ) : (
                mode === 'create' ? 'Создать' : 'Сохранить'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}